import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useVenue } from "@/components/shared/VenueContext";
import { hosysAiApi } from "@/api/hosysai";
import HosysAILayout from "@/components/hosysai/HosysAILayout";
import { ShieldCheck, ArrowRight, Info } from "lucide-react";

const RISK_INFO = {
  low: {
    label: "Baix",
    color: "emerald",
    description: "Sistemes amb ús limitat, sense dades personals crítiques ni decisions automatitzades. Recomanacions: seguiment estàndard.",
  },
  moderate: {
    label: "Moderat",
    color: "amber",
    description: "Sistemes amb dades personals no sensibles. Recomanacions: política interna d'ús + formació bàsica dels usuaris.",
  },
  review_required: {
    label: "Requereix revisió",
    color: "orange",
    description: "Sistemes amb combinacions de factors que requereixen anàlisi específica. Recomanacions: DPIA, formació avançada, supervisió reforçada.",
  },
  high: {
    label: "Risc important",
    color: "red",
    description: "Sistemes que impacten decisions personals rellevants (RRHH, crèdit, biometria) o dades sensibles. Recomanacions: avaluació jurídica, mecanismes de supervisió humana obligatòria.",
  },
};

const COLOR_CLASSES = {
  emerald: "border-emerald-400/30 bg-emerald-500/5 text-emerald-300",
  amber:   "border-amber-400/30 bg-amber-500/5 text-amber-300",
  orange:  "border-orange-400/30 bg-orange-500/5 text-orange-300",
  red:     "border-red-400/30 bg-red-500/5 text-red-300",
};

export default function HosysAIRisk() {
  const { currentVenue, loading: venueLoading } = useVenue();
  const venueId = currentVenue?.venue_id || currentVenue?.id;
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const items = await hosysAiApi.listSystems(venueId);
      setSystems(items || []);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const grouped = ["high", "review_required", "moderate", "low"].reduce((acc, k) => {
    acc[k] = systems.filter((s) => (s.risk_level || "low") === k);
    return acc;
  }, {});

  return (
    <HosysAILayout>
      <div className="space-y-6" data-testid="hosysai-risk-page">
        <div>
          <p className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">Classificació de risc</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mt-1">Visió per nivells de risc</h1>
          <p className="text-slate-400 text-sm mt-1">Els sistemes es classifiquen automàticament segons les característiques introduïdes a l'inventari.</p>
        </div>

        <div className="p-3 rounded-xl border border-cyan-400/20 bg-cyan-500/5 text-xs text-slate-300 flex items-start gap-2">
          <Info className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
          La classificació és <strong>orientativa</strong> i no substitueix una avaluació jurídica o tècnica específica de compliment amb l'AI Act.
        </div>

        {loading || venueLoading ? (
          <div className="h-40 flex items-center justify-center text-slate-400">Carregant…</div>
        ) : (
          <div className="space-y-4">
            {["high", "review_required", "moderate", "low"].map((k) => {
              const info = RISK_INFO[k];
              const list = grouped[k];
              return (
                <div key={k} className={`p-5 rounded-2xl border ${COLOR_CLASSES[info.color]}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <p className="font-bold text-white text-lg flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5" />
                        {info.label}
                      </p>
                      <p className="text-sm text-slate-300 mt-1">{info.description}</p>
                    </div>
                    <span className="text-3xl font-bold tabular-nums">{list.length}</span>
                  </div>
                  {list.length > 0 && (
                    <div className="mt-4 space-y-1.5">
                      {list.slice(0, 5).map((s) => (
                        <Link
                          key={s.ai_system_id} to="/HosysAI/Systems"
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-slate-200 border border-white/5"
                          data-testid={`risk-system-${s.ai_system_id}`}
                        >
                          <span className="flex-1 truncate">{s.name}</span>
                          <span className="text-xs text-slate-400 truncate">{s.provider || "—"}</span>
                          <ArrowRight className="w-3 h-3 text-slate-400" />
                        </Link>
                      ))}
                      {list.length > 5 && (
                        <p className="text-xs text-slate-400 px-3">+ {list.length - 5} més</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </HosysAILayout>
  );
}
